// Admin: Promotion templates manager (Item 1), promotion log (Item 2)
// and vendor WhatsApp outreach log (Item 3). All three live behind /admin.
import { useEffect, useState } from "react";
import {
  Loader2, Plus, Pencil, Trash2, AlertTriangle, RotateCcw, Download,
  MessageSquare, Sparkles, X,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const CARD = { background: "#141414", border: "0.5px solid #222", borderRadius: 14, padding: 14 };
const GOLD = "#E8B84B";
const CREAM = "#F5F1E8";
const MUTED = "#9A9490";

// ---------------------------------------------------------------------------
// Item 1 — Promotion templates manager
// ---------------------------------------------------------------------------
export function AdminRecommendationTemplates() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null); // null | {} (new) | row (edit)
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/promotion-templates").then(({ data }) => setRows(data || []));
  useEffect(() => { load(); }, []);

  const onEdit = (row) => { setEditing(row); setText(row.text); };
  const onNew  = () => { setEditing({}); setText(""); };

  const save = async () => {
    if (!text.trim() || text.length > 160) return;
    setSaving(true);
    try {
      if (editing?.id) {
        await api.patch(`/admin/promotion-templates/${editing.id}`, { text });
        toast.success("Template updated");
      } else {
        await api.post(`/admin/promotion-templates`, { text });
        toast.success("Template created");
      }
      setEditing(null); setText(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setSaving(false); }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete template "${row.text}"?`)) return;
    try { await api.delete(`/admin/promotion-templates/${row.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const custom = rows.filter((r) => !r.default);
  return (
    <div data-testid="admin-rec-templates" style={{ color: CREAM }}>
      <div className="flex items-center justify-between mb-4">
        <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 28 }}>Promotion templates</h1>
        <button data-testid="rec-template-new" onClick={onNew}
                className="inline-flex items-center gap-1.5 rounded-full px-4"
                style={{ background: GOLD, color: "#0B0B0B", minHeight: 48, fontFamily: "'DM Sans', sans-serif", fontSize: 13 }}>
          <Plus className="w-4 h-4" strokeWidth={1.5}/> Add new template
        </button>
      </div>
      <p style={{ color: MUTED, fontFamily: "'DM Sans', sans-serif", fontSize: 13, marginBottom: 16 }}>
        Custom templates show up in every business and vendor promotion composer alongside the defaults.
      </p>

      {custom.length === 0 ? (
        <div data-testid="rec-template-empty" style={{ ...CARD, textAlign: "center", padding: 32 }}>
          <Sparkles className="w-6 h-6 mx-auto" strokeWidth={1.5} style={{ color: GOLD }}/>
          <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 18, marginTop: 8 }}>No custom templates yet</div>
          <div style={{ color: MUTED, fontSize: 13, marginTop: 6, fontFamily: "'DM Sans', sans-serif" }}>
            Businesses will see the default suggestions.
          </div>
        </div>
      ) : (
        <ul className="space-y-2">
          {custom.map((r) => (
            <li key={r.id} data-testid={`rec-template-${r.id}`} className="flex items-start gap-3" style={CARD}>
              <MessageSquare className="w-4 h-4 mt-1 shrink-0" strokeWidth={1.5} style={{ color: GOLD }}/>
              <div className="flex-1 min-w-0" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14 }}>{r.text}</div>
              <button onClick={() => onEdit(r)} className="p-2 hover:opacity-80" style={{ color: MUTED }} data-testid={`rec-template-edit-${r.id}`} aria-label="Edit">
                <Pencil className="w-4 h-4" strokeWidth={1.5}/>
              </button>
              <button onClick={() => remove(r)} className="p-2 hover:opacity-80" style={{ color: "#e08989" }} data-testid={`rec-template-delete-${r.id}`} aria-label="Delete">
                <Trash2 className="w-4 h-4" strokeWidth={1.5}/>
              </button>
            </li>
          ))}
        </ul>
      )}

      {rows.some((r) => r.default) && custom.length > 0 && (
        <p style={{ color: MUTED, fontSize: 11, marginTop: 16, fontFamily: "'DM Sans', sans-serif" }}>
          {rows.filter(r => r.default).length} default suggestions remain available to all senders.
        </p>
      )}

      {editing && (
        <TemplateSheet
          editing={editing}
          text={text}
          setText={setText}
          onSave={save}
          onCancel={() => { setEditing(null); setText(""); }}
          saving={saving}
        />
      )}
    </div>
  );
}

function TemplateSheet({ editing, text, setText, onSave, onCancel, saving }) {
  const len = text.length;
  const tooLong = len > 160;
  return (
    <>
      <button aria-label="Close" onClick={onCancel} className="fixed inset-0 z-[80]"
              style={{ background: "rgba(0,0,0,0.72)" }} data-testid="rec-template-scrim"/>
      <div data-testid="rec-template-sheet"
           className="fixed left-0 right-0 bottom-0 z-[81] md:max-w-md md:left-1/2 md:-translate-x-1/2 md:bottom-12 md:rounded-2xl"
           style={{ background: "#171717", borderTop: "1px solid rgba(232,184,75,0.3)",
                    borderTopLeftRadius: 20, borderTopRightRadius: 20,
                    paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 16px)" }}>
        <div className="grid place-items-center pt-3 pb-2">
          <span style={{ width: 44, height: 4, borderRadius: 9999, background: "rgba(232,184,75,0.45)" }} />
        </div>
        <div className="px-6 pt-1 pb-4 flex items-start justify-between gap-3">
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 22 }}>{editing.id ? "Edit template" : "New template"}</h2>
          <button onClick={onCancel} className="p-2 -mr-2" style={{ color: MUTED }} aria-label="Close">
            <X className="w-5 h-5" strokeWidth={1.5}/>
          </button>
        </div>
        <div className="px-6">
          <div className="flex items-center justify-between mb-2">
            <span style={{ color: GOLD, fontFamily: "'DM Sans', sans-serif", fontSize: 9, letterSpacing: 2, textTransform: "uppercase" }}>Template text</span>
            <span style={{ color: tooLong ? "#e08989" : MUTED, fontSize: 11 }} data-testid="rec-template-counter">{len} / 160</span>
          </div>
          <textarea
            data-testid="rec-template-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={4}
            maxLength={200}
            className="form-input w-full"
            placeholder="We have something special tonight…"
            style={{ background: "#0B0B0B", color: CREAM, border: "1px solid rgba(232,184,75,0.3)" }}
          />
        </div>
        <div className="mt-6 px-6 pt-4 pb-2 flex items-center gap-3" style={{ borderTop: "1px solid rgba(232,184,75,0.15)" }}>
          <button onClick={onCancel} className="flex-1" style={{ minHeight: 48, color: MUTED, fontFamily: "'DM Sans', sans-serif", fontSize: 13 }} data-testid="rec-template-cancel">
            Cancel
          </button>
          <button disabled={tooLong || !text.trim() || saving} onClick={onSave}
                  className="flex-[2] rounded-full inline-flex items-center justify-center gap-1.5"
                  style={{ minHeight: 48, background: GOLD, color: "#0B0B0B",
                           opacity: (tooLong || !text.trim() || saving) ? 0.5 : 1,
                           fontFamily: "'DM Sans', sans-serif", fontSize: 14 }}
                  data-testid="rec-template-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5}/> : null}
            Save
          </button>
        </div>
      </div>
    </>
  );
}


// ---------------------------------------------------------------------------
// Item 2 — Promotion log
// ---------------------------------------------------------------------------
const REC_BASE = process.env.REACT_APP_BACKEND_URL?.replace(/\/$/, "") || "";

export function AdminRecommendationLog() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [ownerKind, setOwnerKind] = useState("");
  const [minReports, setMinReports] = useState(0);
  const [suspendedOnly, setSuspendedOnly] = useState(false);
  const [sort, setSort] = useState("recent");

  const load = () => {
    setLoading(true);
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (ownerKind) p.set("owner_kind", ownerKind);
    if (minReports) p.set("min_reports", minReports);
    if (suspendedOnly) p.set("suspended_only", "true");
    p.set("sort", sort);
    api.get(`/admin/recommendations?${p}`)
      .then(({ data }) => setRows(data.rows || []))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [q, ownerKind, minReports, suspendedOnly, sort]); // eslint-disable-line

  const remove = async (r) => {
    if (!window.confirm(`Delete promotion from "${r.owner_name}"? The owner will be notified.`)) return;
    try { await api.delete(`/admin/recommendations/${r.id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const reinstate = async (r) => {
    if (!window.confirm(`Reinstate promotion privileges for ${r.owner_name}?`)) return;
    try {
      await api.post(`/admin/recommendations/reinstate`, { owner_kind: r.owner_kind, owner_id: r.owner_id });
      toast.success("Reinstated"); load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const csvHref = (() => {
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (ownerKind) p.set("owner_kind", ownerKind);
    if (minReports) p.set("min_reports", minReports);
    if (suspendedOnly) p.set("suspended_only", "true");
    return `${REC_BASE}/api/admin/recommendations.csv?${p}`;
  })();

  return (
    <div data-testid="admin-rec-log" style={{ color: CREAM }}>
      <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 28, marginBottom: 16 }}>Promotion log</h1>

      <div className="flex flex-wrap gap-2 mb-4" data-testid="rec-log-filters">
        <input className="form-input flex-1 min-w-[180px]" placeholder="Search by sender or message…"
               value={q} onChange={(e) => setQ(e.target.value)} data-testid="rec-log-q"
               style={{ minHeight: 48, background: "#0B0B0B", color: CREAM, border: "1px solid #222" }} />
        <select className="form-input" value={ownerKind} onChange={(e) => setOwnerKind(e.target.value)}
                data-testid="rec-log-kind" style={{ minHeight: 48 }}>
          <option value="">All accounts</option>
          <option value="business">Businesses</option>
          <option value="vendor">Vendors</option>
        </select>
        <select className="form-input" value={minReports} onChange={(e) => setMinReports(Number(e.target.value))}
                data-testid="rec-log-reports" style={{ minHeight: 48 }}>
          <option value={0}>Any reports</option>
          <option value={1}>1+ reports</option>
          <option value={3}>3+ reports (flagged)</option>
        </select>
        <select className="form-input" value={sort} onChange={(e) => setSort(e.target.value)}
                data-testid="rec-log-sort" style={{ minHeight: 48 }}>
          <option value="recent">Most recent</option>
          <option value="reports">Most reports</option>
          <option value="recipients">Most recipients</option>
        </select>
        <label className="inline-flex items-center gap-2 px-3" style={{ minHeight: 48, fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: MUTED }} data-testid="rec-log-suspended-row">
          <input type="checkbox" checked={suspendedOnly} onChange={(e) => setSuspendedOnly(e.target.checked)} data-testid="rec-log-suspended" />
          Suspended only
        </label>
        <a href={csvHref} className="inline-flex items-center gap-1.5 px-4 rounded-full" data-testid="rec-log-csv"
           style={{ background: "rgba(232,184,75,0.12)", color: GOLD, border: "1px solid rgba(232,184,75,0.4)",
                    minHeight: 48, fontFamily: "'DM Sans', sans-serif", fontSize: 13 }}>
          <Download className="w-4 h-4" strokeWidth={1.5}/> Export CSV
        </a>
      </div>

      {loading ? <Loader2 className="w-6 h-6 animate-spin" strokeWidth={1.5} style={{ color: GOLD }}/> :
       rows.length === 0 ? <div style={{ ...CARD, textAlign: "center" }}>No promotions yet</div> :
       <ul className="space-y-2">
         {rows.map((r) => (
           <li key={r.id} data-testid={`rec-log-row-${r.id}`}
               style={{ ...CARD, border: (r.reported || 0) >= 3 ? "1px solid #e08989" : CARD.border }}>
             <div className="flex items-start gap-3">
               <div className="flex-1 min-w-0">
                 <div className="flex items-center gap-2 flex-wrap">
                   <span style={{ fontFamily: "'Playfair Display', serif", fontSize: 15 }}>{r.owner_name}</span>
                   <span className="px-2 rounded-full" style={{ background: "rgba(232,184,75,0.1)", color: GOLD, fontSize: 10, textTransform: "uppercase", letterSpacing: 1 }}>
                     {r.owner_kind}
                   </span>
                   {r.suspended && (
                     <span className="inline-flex items-center gap-1 px-2 rounded-full" data-testid="rec-log-suspended-badge"
                           style={{ background: "rgba(245,158,11,0.1)", color: "#f59e0b", fontSize: 10, textTransform: "uppercase", letterSpacing: 1 }}>
                       <AlertTriangle className="w-3 h-3" strokeWidth={1.5}/> Suspended
                     </span>
                   )}
                 </div>
                 <div style={{ color: CREAM, fontFamily: "'DM Sans', sans-serif", fontSize: 14, marginTop: 6 }}>{r.message}</div>
                 <div className="flex flex-wrap gap-3 mt-2" style={{ color: MUTED, fontFamily: "'DM Sans', sans-serif", fontSize: 11 }}>
                   <span>Audience: {r.audience}</span>
                   <span>Recipients: {r.recipient_count}</span>
                   <span style={{ color: (r.reported || 0) >= 3 ? "#e08989" : MUTED }}>Reports: {r.reported || 0}</span>
                   <span data-testid={`rec-log-revenue-${r.id}`} title="Est. revenue attributed — customer-logged spend within 7 days of receiving recommendation" style={{ color: (r.revenue_attributed || 0) > 0 ? GOLD : MUTED }}>Revenue: {r.revenue_attributed != null && r.revenue_attributed > 0 ? `$${r.revenue_attributed.toFixed(2)}` : "—"}</span>
                   <span>{(r.created_at || "").slice(0, 16).replace("T", " · ")}</span>
                 </div>
               </div>
               <div className="flex flex-col gap-2">
                 {r.suspended && (
                   <button onClick={() => reinstate(r)} data-testid={`rec-log-reinstate-${r.id}`}
                           className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px]"
                           style={{ color: GOLD, border: "1px solid rgba(232,184,75,0.4)" }}>
                     <RotateCcw className="w-3 h-3" strokeWidth={1.5}/> Reinstate
                   </button>
                 )}
                 <button onClick={() => remove(r)} data-testid={`rec-log-delete-${r.id}`}
                         className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px]"
                         style={{ color: "#e08989", border: "1px solid rgba(224,137,137,0.4)" }}>
                   <Trash2 className="w-3 h-3" strokeWidth={1.5}/> Delete
                 </button>
               </div>
             </div>
           </li>
         ))}
       </ul>
      }
      {/* Totals row — sum of attributed revenue across filtered rows */}
      {rows.length > 0 && (
        <div className="mt-4 px-4 py-3 flex justify-end items-center gap-4" data-testid="rec-log-totals"
             style={{ background: "#171717", border: "1px solid rgba(232,184,75,0.25)", borderRadius: 12 }}>
          <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: MUTED, textTransform: "uppercase", letterSpacing: 1.5 }}>
            Total attributed revenue
          </span>
          <span style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, color: GOLD }} data-testid="rec-log-total-revenue">
            ${rows.reduce((acc, r) => acc + (r.revenue_attributed || 0), 0).toFixed(2)}
          </span>
        </div>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Item 3 — Vendor WhatsApp outreach log
// ---------------------------------------------------------------------------
export function AdminVendorInteractions() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("");

  const load = () => {
    setLoading(true);
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (kind) p.set("interaction_type", kind);
    api.get(`/admin/vendor-interactions?${p}`)
      .then(({ data }) => setRows(data.rows || []))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [q, kind]); // eslint-disable-line

  const csvHref = (() => {
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (kind) p.set("interaction_type", kind);
    return `${REC_BASE}/api/admin/vendor-interactions.csv?${p}`;
  })();

  return (
    <div data-testid="admin-vendor-interactions" style={{ color: CREAM }}>
      <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 28, marginBottom: 16 }}>Vendor outreach log</h1>

      <div className="flex flex-wrap gap-2 mb-4">
        <input className="form-input flex-1 min-w-[180px]" placeholder="Search vendor name…"
               value={q} onChange={(e) => setQ(e.target.value)} data-testid="vi-q"
               style={{ minHeight: 48, background: "#0B0B0B", color: CREAM, border: "1px solid #222" }} />
        <select className="form-input" value={kind} onChange={(e) => setKind(e.target.value)}
                data-testid="vi-kind" style={{ minHeight: 48 }}>
          <option value="">All interactions</option>
          <option value="qr_scan">QR scan</option>
          <option value="whatsapp">WhatsApp contact</option>
        </select>
        <a href={csvHref} className="inline-flex items-center gap-1.5 px-4 rounded-full" data-testid="vi-csv"
           style={{ background: "rgba(232,184,75,0.12)", color: GOLD, border: "1px solid rgba(232,184,75,0.4)",
                    minHeight: 48, fontFamily: "'DM Sans', sans-serif", fontSize: 13 }}>
          <Download className="w-4 h-4" strokeWidth={1.5}/> Export CSV
        </a>
      </div>

      {loading ? <Loader2 className="w-6 h-6 animate-spin" strokeWidth={1.5} style={{ color: GOLD }}/> :
       rows.length === 0 ? <div style={{ ...CARD, textAlign: "center" }}>No vendor interactions logged yet</div> :
       <ul className="space-y-2">
         {rows.map((r) => (
           <li key={r.id} data-testid={`vi-row-${r.id}`} style={CARD}>
             <div className="flex items-center justify-between gap-3 flex-wrap">
               <div>
                 <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 15 }}>{r.vendor_name}</div>
                 <div style={{ color: MUTED, fontFamily: "'DM Sans', sans-serif", fontSize: 11, marginTop: 3 }}>
                   {r.user_display}{r.anonymous ? " · anonymous" : ""}
                 </div>
               </div>
               <div className="flex items-center gap-3">
                 <span className="px-2 rounded-full" style={{
                   background: r.interaction_type === "whatsapp" ? "rgba(37,211,102,0.12)" : "rgba(232,184,75,0.12)",
                   color: r.interaction_type === "whatsapp" ? "#7cd2a4" : GOLD,
                   fontSize: 10, textTransform: "uppercase", letterSpacing: 1,
                 }}>
                   {r.interaction_type === "whatsapp" ? "WhatsApp" : "QR scan"}
                 </span>
                 <span style={{ color: MUTED, fontFamily: "'DM Sans', sans-serif", fontSize: 11 }}>
                   {(r.timestamp || "").slice(0, 16).replace("T", " · ")}
                 </span>
               </div>
             </div>
           </li>
         ))}
       </ul>
      }
    </div>
  );
}
